import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useProfiles } from "../profiles/ProfileContext";
import { Button } from "../components/ui/Button";
import { PageHeader } from "../components/Surface";
import { Icon } from "../app/Icon";
import { LOAD_BID_TYPES } from "../lib/loads";
import { buildNotifications, type NotificationItem, type NotifKind } from "../lib/notifications";
import { getRead, markRead } from "../lib/notifRead";
import { usePagedView, ViewToggle, Pagination } from "../components/Paged";

const kindIcon: Record<NotifKind, string> = { message: "messages", application: "applications", job: "bids", system: "verify" };

function ago(at?: string): string {
  if (!at) return "";
  const s = Math.max(0, (Date.now() - +new Date(at)) / 1000);
  if (s < 60) return "now";
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return `${Math.floor(s / 86400)}d`;
}

const TABS: { key: string; label: string; kinds?: NotifKind[]; unread?: boolean }[] = [
  { key: "all", label: "All" },
  { key: "unread", label: "Unread", unread: true },
  { key: "messages", label: "Messages", kinds: ["message"] },
  { key: "applications", label: "Applications", kinds: ["application"] },
  { key: "jobs", label: "Jobs", kinds: ["job"] },
  { key: "system", label: "System", kinds: ["system"] },
];

export function Notifications() {
  const { active } = useProfiles();
  const navigate = useNavigate();
  const [items, setItems] = useState<NotificationItem[]>([]);
  const [read, setRead] = useState<Set<string>>(() => getRead());
  const [tab, setTab] = useState("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    if (!active) {
      setLoading(false);
      return;
    }
    setLoading(true);
    const isBidder = LOAD_BID_TYPES.includes(active.type);
    buildNotifications(active.id, { isDriver: active.type === "driver", isBidder })
      .then((n) => !cancelled && setItems(n))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [active?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const isRead = (id: string) => read.has(id);
  const unreadCount = useMemo(() => items.filter((n) => !isRead(n.id)).length, [items, read]); // eslint-disable-line react-hooks/exhaustive-deps

  function count(t: (typeof TABS)[number]): number {
    if (t.unread) return unreadCount;
    if (!t.kinds) return items.length;
    return items.filter((n) => t.kinds!.includes(n.kind)).length;
  }

  const shown = items.filter((n) => {
    const t = TABS.find((x) => x.key === tab)!;
    if (t.unread) return !isRead(n.id);
    if (!t.kinds) return true;
    return t.kinds.includes(n.kind);
  });
  const { view, page, pageCount, pageItems, setView, setPage } = usePagedView(shown, 15, "list");

  function open(n: NotificationItem) {
    markRead([n.id]);
    setRead(getRead());
    navigate(n.href);
  }
  function markAll() {
    markRead(items.map((n) => n.id));
    setRead(getRead());
  }

  return (
    <div className="grid gap-6">
      <PageHeader
        title="Notifications"
        subtitle="Activity across your applications, bids, messages, and account."
        actions={<Button variant="ghost" onClick={markAll}><span className="flex items-center gap-1.5"><Icon name="applications" className="h-4 w-4" /> Mark all read</span></Button>}
      />

      <div className="flex flex-wrap gap-1 border-b border-line">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setTab(t.key)}
            className={`-mb-px border-b-2 px-3 py-2 text-sm font-bold transition-colors ${tab === t.key ? "border-accent text-accent-strong" : "border-transparent text-muted hover:text-ink"}`}
          >
            {t.label} ({count(t)})
          </button>
        ))}
      </div>

      <div className="flex justify-end"><ViewToggle view={view} onChange={setView} /></div>

      {loading ? (
        <p className="font-mono text-sm text-muted">Loading…</p>
      ) : shown.length === 0 ? (
        <p className="font-mono text-sm text-muted">You're all caught up.</p>
      ) : (
        <ul className={view === "grid" ? "grid gap-2 sm:grid-cols-2" : "grid gap-2"}>
          {pageItems.map((n) => {
            const unread = !isRead(n.id);
            return (
              <li key={n.id}>
                <button
                  type="button"
                  onClick={() => open(n)}
                  className={`flex w-full items-center gap-4 rounded-card border p-4 text-left transition-colors hover:border-accent ${unread ? "border-line bg-surface" : "border-line bg-surface/60"}`}
                >
                  <span className={`h-2 w-2 shrink-0 rounded-full ${unread ? "bg-accent" : "bg-transparent"}`} aria-hidden />
                  <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-surface-2 text-muted"><Icon name={kindIcon[n.kind]} className="h-4 w-4" /></span>
                  <span className="min-w-0 flex-1">
                    <span className={`block truncate text-sm ${unread ? "font-bold text-ink" : "text-ink"}`}>{n.title}</span>
                    {n.body && <span className="block truncate font-mono text-xs text-muted">{n.body}</span>}
                  </span>
                  <span className="shrink-0 font-mono text-xs text-muted">{ago(n.at)}</span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
      <Pagination page={page} pageCount={pageCount} onPage={setPage} />
    </div>
  );
}
